import React, { useContext, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "material-ui-confirm";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Link,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  findGetParameter,
  postData,
} from "../../components/utilities/UtilityFunctions";
import GenericPicker from "../../components/utilities/GenericPicker";

import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import { LocationTextField } from "../../components/utilities/LocationPicker";
import { globalVariables } from "../../global/GlobalVariables";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function OrganizationRegister(props) {
  const { organizationType } = props;
  const authContext = useContext(AuthContext);

  const confirm = useConfirm();
  const navigate = useNavigate();
  const globalVars = globalVariables();

  const [loading, setLoading] = useState(false);

  return (
    <>
      {Boolean(loading) && <ModalLoadingFull />}
      <Box sx={{ width: "100%" }}>
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Formik
            initialValues={{
              coupon: findGetParameter("coupon") || "",
              user: {
                email: authContext?.authState?.identity?.email || "",
                firstName:
                  authContext?.authState?.identity?.firstName ||
                  authContext?.authState?.identity?.given_name ||
                  "",
                lastName:
                  authContext?.authState?.identity?.lastName ||
                  authContext?.authState?.identity?.family_name ||
                  "",
              },
              organization: {
                name: "",
                location: {
                  formatted_address: "",
                },
                websiteURL: "",
                organizationTypes: {
                  store: true,
                  club: false,
                },
                onboarding: {
                  productRanges: {
                    TCG: false,
                    Miniatures: false,
                    RPGs: false,
                    BoardGames: false,
                  },
                  pointOfSale: "",
                  pointOfSaleOther: "",
                },
              },
            }}
            validationSchema={Yup.object().shape({
              coupon: Yup.string().max(255).required("Required"),
              user: Yup.object().shape({
                email: Yup.string()
                  .max(128)
                  .email("Must be a valid email")
                  .required("Required"),
                firstName: Yup.string().required("Required"),
                lastName: Yup.string().required("Required"),
              }),
              organization: Yup.object().shape({
                name: Yup.string()
                  .min(3, "Minimum 3 Characters!")
                  .max(255, "Maximum 255 Characters!")
                  .required("Required"),
                location: Yup.object().shape({
                  formatted_address: Yup.string().required("Required"),
                }),
                onboarding: Yup.object().shape({
                  pointOfSale: Yup.string().required("Required"),
                }),
              }),
            })}
            onSubmit={async (values) => {
              try {
                await confirm({
                  title: `Terms of Service.`,
                  description: <TermsOfServiceBox />,
                  dialogProps: {
                    sx: {
                      zIndex: 99999,
                    },
                  },
                  confirmationText: "I Agree",
                  cancellationText: "Cancel",
                });
              } catch (err) {
                return;
              }

              setLoading(true);
              try {
                var apiGameStore = await postData(`/gamestores`, {
                  body: values,
                });
                setLoading(false);
                postNotification(
                  "success",
                  `New ${
                    organizationType === "store" ? "Store" : "Organization"
                  } created`
                );
                if (
                  authContext?.authState?.identity?.isAdmin === true ||
                  authContext?.authState?.identity?.userId ===
                    apiGameStore.ownerId
                ) {
                  navigate(`/organize/organization/${apiGameStore.id}`);
                } else if (!authContext?.authState?.identity?.userId) {
                  postNotification(
                    "info",
                    `As a new user to ${globalVars?.site?.title} you will need to verify your new account.`
                  );
                  navigate(
                    `/signup?email=${values.user.email}&firstName=${values.user.firstName}&lastName=${values.user.lastName}&redirect=/organize/organization/${apiGameStore.id}`
                  );
                } else {
                  navigate(`/organization/${apiGameStore.id}`);
                }
              } catch (err) {
                postNotification("error", err);
              }
              setLoading(false);
              return;
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container sx={{ gap: 2 }}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                    }}
                  >
                    <Typography variant="h3">
                      {organizationType === "store"
                        ? "Register Your Store"
                        : "Register Your Organization"}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                    }}
                  >
                    <Typography variant="h4" sx={{ mb: 2 }}>
                      {organizationType === "store"
                        ? "Store Details"
                        : "Organization Details"}
                    </Typography>

                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          name="coupon"
                          label="Coupon Code"
                          value={values.coupon}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.coupon && errors.coupon)}
                          helperText={touched.coupon && errors.coupon}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="organization.name"
                          label="Organization Name"
                          value={values.organization?.name}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.organization?.name &&
                              errors.organization?.name
                          )}
                          helperText={
                            touched.organization?.name &&
                            errors.organization?.name
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <LocationTextField
                          location={values.organization?.location}
                          required
                          setLocation={(location) => {
                            setFieldValue("organization.location", location);
                          }}
                          error={Boolean(
                            touched.organization?.location?.formatted_address &&
                              errors.organization?.location?.formatted_address
                          )}
                          helperText={
                            touched.organization?.location?.formatted_address &&
                            errors.organization?.location?.formatted_address
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="organization.websiteURL"
                          label="Website URL"
                          value={values.organization?.websiteURL}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.organization?.websiteURL &&
                              errors.organization?.websiteURL
                          )}
                          helperText={
                            touched.organization?.websiteURL &&
                            errors.organization?.websiteURL
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <GenericPicker
                          name="organization.onboarding.pointOfSale"
                          label={"Point of Sale"}
                          value={values.organization.onboarding.pointOfSale}
                          inclAny={false}
                          handleChange={(e) => {
                            if (
                              e.target.value !==
                              values.organization.onboarding.pointOfSale
                            ) {
                              setFieldValue(
                                "organization.onboarding.pointOfSaleOther",
                                ""
                              );
                            }

                            handleChange(e);
                          }}
                          required={true}
                          touched={
                            touched.organization?.onboarding?.pointOfSale
                          }
                          error={Boolean(
                            touched.organization?.onboarding?.pointOfSale &&
                              errors.organization?.onboarding?.pointOfSale
                          )}
                          list={[
                            "Square",
                            "Clover",
                            "Crystal Commerce",
                            "Shoppify",
                            "BinderPOS",
                            "Other",
                          ]}
                        />
                      </Grid>

                      {values.organization.onboarding.pointOfSale ===
                      "Other" ? (
                        <Grid item xs={12}>
                          <TextField
                            name="organization.onboarding.pointOfSaleOther"
                            label="Point of Sale"
                            value={
                              values.organization.onboarding.pointOfSaleOther
                            }
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            required
                            onBlur={handleBlur}
                            onChange={handleChange}
                          />
                        </Grid>
                      ) : (
                        <></>
                      )}

                      <Grid item xs={12}>
                        <Typography variant="h4">
                          Please select what product ranges your store carries.
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          label={"TCG"}
                          control={
                            <Checkbox
                              name="organization.onboarding.productRanges.TCG"
                              checked={
                                values.organization.onboarding.productRanges.TCG
                              }
                              defaultChecked={false}
                              onChange={(e) => {
                                setFieldValue(
                                  "organization.onboarding.productRanges.TCG",
                                  !values.organization.onboarding.productRanges
                                    .TCG
                                );
                              }}
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          label={"Miniatures"}
                          control={
                            <Checkbox
                              name="organization.onboarding.productRanges.Miniatures"
                              checked={
                                values.organization.onboarding.productRanges
                                  .Miniatures
                              }
                              defaultChecked={false}
                              onChange={(e) => {
                                setFieldValue(
                                  "organization.onboarding.productRanges.Miniatures",
                                  !values.organization.onboarding.productRanges
                                    .Miniatures
                                );
                              }}
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          label={"RPGs"}
                          control={
                            <Checkbox
                              name="organization.onboarding.productRanges.RPGs"
                              checked={
                                values.organization.onboarding.productRanges
                                  .RPGs
                              }
                              defaultChecked={false}
                              onChange={(e) => {
                                setFieldValue(
                                  "organization.onboarding.productRanges.RPGs",
                                  !values.organization.onboarding.productRanges
                                    .RPGs
                                );
                              }}
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          label={"BoardGames"}
                          control={
                            <Checkbox
                              name="organization.onboarding.productRanges.BoardGames"
                              checked={
                                values.organization.onboarding.productRanges
                                  .BoardGames
                              }
                              defaultChecked={false}
                              onChange={(e) => {
                                setFieldValue(
                                  "organization.onboarding.productRanges.BoardGames",
                                  !values.organization.onboarding.productRanges
                                    .BoardGames
                                );
                              }}
                            />
                          }
                        />
                      </Grid>
                    </Grid>

                    {/* Please add a drop down or checklist, whatever is best for what type of product they carry:
TCG
Miniatures
RPGs
Board Games


NEW

12:58
and, Point of Sale system they use:
Square
Clover
Crystal Commerce
other - fill in option? */}

                    <Typography variant="h4" sx={{ mt: 2, mb: 2 }}>
                      User Details
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          name="user.email"
                          label="Email"
                          value={values.user?.email}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={(e) => {
                            const trimmedString = e.target.value
                              .trim()
                              .toLowerCase();
                            setFieldValue("user.email", trimmedString);
                          }}
                          error={Boolean(
                            touched.user?.email && errors.user?.email
                          )}
                          helperText={touched.user?.email && errors.user?.email}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="user.firstName"
                          label="First Name"
                          value={values.user?.firstName}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.user?.firstName && errors.user?.firstName
                          )}
                          helperText={
                            touched.user?.firstName && errors.user?.firstName
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="user.lastName"
                          label="Last Name"
                          value={values.user?.lastName}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.user?.lastName && errors.user?.lastName
                          )}
                          helperText={
                            touched.user?.lastName && errors.user?.lastName
                          }
                        />
                      </Grid>
                    </Grid>

                    <Button
                      fullWidth
                      color="success"
                      variant="contained"
                      type="submit"
                      sx={{ mt: 2 }}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      </Box>
    </>
  );
}

const TermsOfServiceBox = () => {
  return (
    <Box>
      <Typography variant="caption" paragraph>
        By clickin "I agree", you agree to the Terms of Service for the{" "}
        {getEnvironmentVariable("COMPANY_NAME")} Website or Services, EULA, and
        Privacy Policy. You must be 13 years of age or older to use{" "}
        {getEnvironmentVariable("COMPANY_NAME")}. By agreeing you confirm that
        you are over 18, or if you are between 13 and 18 years of age, you have
        your parent or legal guardian's permission to use{" "}
        {getEnvironmentVariable("COMPANY_NAME")}
        We take privacy seriously and collect the following information to
        administer your account, scores, rankings and ticket sales. Your name is
        shared publicly as part of event rosters, pairings, placings and
        rankings. Your email address is kept private and used as our unique
        identifier and only shared with the Event Organizers of events you
        participate in, Team owners which you are a member of, and Vendors you
        make purchases from. {getEnvironmentVariable("COMPANY_NAME")} may
        contact you occasionally in relation to services requested.
      </Typography>
      <Typography variant="caption">
        Visit these links for our complete{" "}
        <Link href="/privacy" target="_blank">
          Privacy Policy
        </Link>{" "}
        and{" "}
        <Link href="/eula" target="_blank">
          EULA
        </Link>
        .
      </Typography>
    </Box>
  );
};
